import {computed, inject, Injectable, signal} from "@angular/core";
import {Subject} from "rxjs";
import {TreeNode} from "../kd-workplace/kd-workplace.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {KnowledgeBaseService} from "./knowledge-base.service";
import {Category} from "src/app/shared/interfaces/category";
import {Malfunction} from "src/app/shared/interfaces/malfunction";
import {Solution} from "src/app/shared/interfaces/solution";

interface ItemEditStore {
  itemBeingEdited: TreeNode | null;
  modalHeading: string;
}

export type ActionType =
  | "addCategory"
  | "editCategory"
  | "addMalfunction"
  | "editMalfunction"
  | "addSolution"
  | "editSolution"
  | "";

@Injectable({
  providedIn: "root",
})
export class EditTreeItemService {
  private knowledgeBaseService = inject(KnowledgeBaseService);
  public itemBeingEdited$ = new Subject<TreeNode | null>();
  /**
   * subject to set the heading of itemFormModal
   */
  public modalHeading$ = new Subject<string>();
  /**
   * Signal to set the action triggered by form submission
   *
   * Possible values:
   * - `"addCategory"` - add item in category level
   * - `"addMalfunction"` - add item in malfunction level
   * - `"addSolution"` - add item in solution level
   * - `"editCategory"` - edit item in category level
   * - `"editMalfunction"` - edit item in category level
   * - `"editSolution"` - edit item in category level
   */
  public action = signal<ActionType>("");

  private state = signal<ItemEditStore>({
    itemBeingEdited: null,
    modalHeading: "",
  });

  itemBeingEdited = computed(() => this.state().itemBeingEdited);
  modalHeading = computed(() => this.state().modalHeading);

  constructor() {
    this.itemBeingEdited$.pipe(takeUntilDestroyed()).subscribe((item) => {
      this.state.update((state) => ({
        ...state,
        itemBeingEdited: item,
      }));
      switch (item?.type) {
        case "category":
          this.action.set("editCategory");
          this.modalHeading$.next("Prozessschritt bearbeiten");
          break;
        case "malfunction":
          this.action.set("editMalfunction");
          this.modalHeading$.next("Störungsmeldung bearbeiten");
          break;
        case "solution":
          this.action.set("editSolution");
          this.modalHeading$.next("Lösung bearbeiten");
          break;
        default:
          this.modalHeading$.next("Eintrag bearbeiten");
      }
    });
    this.modalHeading$.subscribe((heading) =>
      this.state.update((state) => ({
        ...state,
        modalHeading: heading,
      }))
    );
  }

  addItem(item: Partial<Category | Malfunction | Solution>) {
    this.knowledgeBaseService.add$.next({
      item: item,
      level: this.action(),
    });
  }

  updateItem(item: TreeNode) {
    const updatedItem = {
      ...this.itemBeingEdited(),
      ...item,
    };
    this.knowledgeBaseService.update$.next(updatedItem);
    this.itemBeingEdited$.next(null);
  }
}
